import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { withoutDetailRelationship } from "../../utils/entityAddress";
import { rememberHome, rememberedHome } from "./homeMemory";
import {
  ValueChainState,
  changeValueChainState,
  decodeValueChainState,
  encodeValueChainState,
} from "./valueChainState";

/**
 * The value chain's state, read from the address, and a function that
 * changes it.
 *
 * Every change is a new entry of the browser's history, so the back button
 * steps through the states. The home jurisdiction is remembered for the
 * signed-in user and is the default when the address names none.
 */
export function useValueChainState(): [
  ValueChainState,
  (change: Partial<ValueChainState>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = useAuth().user?.id ?? null;

  const state = useMemo(() => {
    const decoded = decodeValueChainState(searchParams);
    return decoded.home
      ? decoded
      : { ...decoded, home: rememberedHome(userId) };
  }, [searchParams, userId]);

  const change = useCallback(
    (part: Partial<ValueChainState>) => {
      if ("home" in part) {
        rememberHome(userId, part.home ?? null);
      }
      setSearchParams((current) => {
        const next = encodeValueChainState(
          changeValueChainState(decodeValueChainState(current), part),
          current,
        );
        // A detail opened or closed replaces a relationship open over it
        return "detail" in part ? withoutDetailRelationship(next) : next;
      });
    },
    [setSearchParams, userId],
  );

  return [state, change];
}
